import { SERVER_HOSTS } from '@core/constant/servers';
import { isTouchDevice } from '@core/mobile/isTouchDevice';
import { virtualKeyboardVisible } from '@core/mobile/virtualKeyboard';
import ArrowLeft from '@phosphor/arrow-left.svg';
import ArrowRight from '@phosphor/arrow-right.svg';
import { authServiceClient } from '@service-auth/client';
import { createCallback } from '@solid-primitives/rootless';
import { action, useSearchParams, useSubmission } from '@solidjs/router';
import { cn } from '@ui';
import { platformFetch } from 'core/util/platformFetch';
import { createEffect, createSignal, Show, untrack } from 'solid-js';
import { ErrorMsg, Input, Stage } from './Shared';

// Construct the redirect uri to use for passwordless login.
// This will send us back to the application after clicking the magic link.
// in "dev" (local) we use http otherwise https
const protocol = import.meta.hot ? 'http' : 'https';
const REDIRECT_URI = `${protocol}://${window.location.host}/app`;

async function isPasswordLogin(email?: string | null) {
  if (!email) return false;

  const encodedEmail = new TextEncoder().encode(email.toLowerCase());
  const hashedBuffer = await crypto.subtle.digest('SHA-256', encodedEmail);
  const hashedEmail = Array.from(new Uint8Array(hashedBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return (
    hashedEmail ===
    '0d10222b5594dbb0eb5d2bccbc9b5d8e9ff83e99421b573fb32c8a7b74491c81'
  );
}

// Initiates the passwordless login flow.
// Redirecting to the requested identity provider endpoint.
export const sendEmailCode = action(async (formData: FormData) => {
  const email = formData.get('email');
  if (!email || typeof email !== 'string') throw new Error('Invalid email');

  if (typeof email === 'string' && (await isPasswordLogin(email))) {
    const password = formData.get('password');
    if (!password || typeof password !== 'string') return 'isPasswordLogin';

    const maybeTokens = await authServiceClient.passwordLogin({
      password,
      email,
    });
    if (maybeTokens.isErr())
      throw new Error(
        'Failed to login. Check your email and password then try again.'
      );

    return 'LoggedIn';
  }

  const url = new URL(window.location.href);
  const referral_code = url.searchParams.get('referral_code');

  const response = await platformFetch(
    `${SERVER_HOSTS['auth-service']}/login/passwordless`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        redirect_uri: REDIRECT_URI,
        email,
        ...(referral_code && { referral_code }),
      }),
    }
  );

  if (!response.ok) throw new Error(await response.text());

  // If the passwordless call returns 202,
  // the email needs to login through a dedicated identity provider.
  if (response.status === 202) {
    const body = await response.json();
    const idp_id = body.idp_id;
    const ssoUrl = new URL(`${SERVER_HOSTS['auth-service']}/login/sso`);
    ssoUrl.searchParams.set('idp_id', idp_id);
    ssoUrl.searchParams.set('login_hint', (email ?? '').toString());
    if (referral_code) ssoUrl.searchParams.set('referral_code', referral_code);
    window.location.href = ssoUrl.toString();
    return false; // passwordless login flow is not reached
  }

  return true;
}, 'passwordless-login');

export function useResetEmailCode(setStage: (next: Stage) => void) {
  const submission = useSubmission(sendEmailCode);
  return createCallback(() => {
    submission.clear();
    setStage(Stage.Email);
  });
}

export function EmailForm(props: { setStage: (next: Stage) => void }) {
  const [isPasswordLogin, setIsPasswordLogin] = createSignal(false);
  const submission = useSubmission(sendEmailCode);
  const [searchParams] = useSearchParams();
  const searchParamsEmail = untrack(() => {
    const email = searchParams.email;
    if (typeof email === 'string') return email;
  });

  createEffect(() => {
    if (submission.result === true) {
      props.setStage(Stage.Verify);
    } else if (submission.result === 'isPasswordLogin') {
      setIsPasswordLogin(true);
    } else if (submission.result === 'LoggedIn') {
      props.setStage(Stage.Done);
    }
  });

  const handleBack = createCallback(() => {
    submission.clear();
    props.setStage(Stage.None);
  });

  return (
    <div class="grid select-none">
      <form action={sendEmailCode} method="post" class="m-0">
        <div
          class={cn(
            'flex items-center justify-center text-center py-4 px-6 border-y border-edge-muted',
            virtualKeyboardVisible() && 'border-t border-edge-muted'
          )}
        >
          <Input
            value={searchParamsEmail}
            placeholder="Email Address"
            type="email"
            id="email"
          />
        </div>

        <Show when={isPasswordLogin()}>
          <div class="grid items-center justify-center py-4 px-6 border-b border-edge-muted">
            <Input
              required={isPasswordLogin()}
              placeholder="Password"
              type="password"
              id="password"
            />
          </div>
        </Show>

        <div class="border-b border-edge-muted py-4 px-6 flex flex-none justify-between items-center">
          <button
            class="hover:text-accent hover:transition-none transition-colors duration-300 grid grid-cols-[min-content_min-content] gap-1.5 items-center w-min"
            onClick={() => {
              if (isTouchDevice()) return;
              handleBack();
            }}
            // Using onPointerDown so that on touch device able to interact with button before closing virtual keyboard
            onPointerDown={(e) => {
              if (!isTouchDevice()) return;
              e.stopPropagation();
              e.preventDefault();
              handleBack();
            }}
            type="button"
          >
            <ArrowLeft class="size-5" />
            <span>Back</span>
          </button>

          <button
            class="hover:text-accent hover:transition-none transition-colors duration-300 grid grid-cols-[min-content_min-content] gap-1.5 items-center w-min"
            type="submit"
            disabled={submission.pending}
            onClick={(e) => {
              if (isTouchDevice()) e.preventDefault();
            }}
            // Using onPointerDown so that on touch device able to interact with button before closing virtual keyboard
            onPointerDown={(e) => {
              if (!isTouchDevice()) return;
              e.stopPropagation();
              e.preventDefault();
              e.currentTarget.form?.requestSubmit();
            }}
          >
            <span>Continue</span>
            <ArrowRight class="size-5" />
          </button>
        </div>
        <ErrorMsg msg={submission.error?.message} />
      </form>
    </div>
  );
}
